<script setup lang="ts">
import { ref } from "vue";
import moment from "moment";
import { isNullish, TuTable, TuTableHeader, TuTableRow } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel";
import type { PendingTeamInvitationData } from "@Modules/teams/Data/Models/PendingTeamInvitation";
import useTranslation from "@Composables/useTranslation";
import OrgUserPendingActionDropdown from "@Modules/teams/components/OrgUserPendingActionDropdown.vue";
import Capitalize from "@Resources/js/filters/Capitalize";

//
// Props
interface Props {
  organization: Organization
  users: PendingTeamInvitationData[]
  loading?: boolean
  disabled?: boolean
  resendInvitation?: (user: PendingTeamInvitationData) => Promise<void>
  cancelInvitation?: (user: PendingTeamInvitationData) => Promise<void>
}

const props = withDefaults(defineProps<Props>(), {
  loading: true,
  disabled: false,
  resendInvitation: async () => Promise.resolve(),
  cancelInvitation: async () => Promise.resolve(),
});

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Data
const busyWith = ref<string | null>(null);

//
// Methods
function formatDateInvited(date?: string | null): string {
  if (isNullish(date)) {
    return "—";
  }

  return moment.utc(date).local().format("MMM D, YYYY");
}

function doCancel(user: PendingTeamInvitationData): void {
  busyWith.value = user.email;

  props.cancelInvitation(user)
    .finally(() => busyWith.value = null);
}

function doResend(user: PendingTeamInvitationData): void {
  busyWith.value = user.email;

  props.resendInvitation(user)
    .finally(() => busyWith.value = null);
}

</script>

<template>
  <div class="org-user-pending-table _pending_table">
    <TuTable>
      <template
        v-if="!isMobileView"
        #header
      >
        <TuTableHeader>
          <th>{{ __('organization_users_settings.table_email') }}</th>
          <th class="org-user-pending-table__narrow-cell">
            {{ __('organization_users_settings.table_invited_as') }}
          </th>
          <th class="org-user-pending-table__narrow-cell">
            {{ __('organization_users_settings.table_date_invited') }}
          </th>
          <th class="org-user-pending-table__action-cell">
            {{ __('organization_users_settings.table_action') }}
          </th>
        </TuTableHeader>
      </template>

      <TuTableRow
        v-if="loading"
        class="_loading"
      >
        <td colspan="4">
          <i class="fa fa-spinner fa-spin" />
          {{ __('organization_users_settings.table_loading') }}
        </td>
      </TuTableRow>

      <TuTableRow v-else-if="!users.length">
        <td
          class="_no_users text-center"
          colspan="4"
        >
          <i class="fa-solid fa-circle-exclamation" />
          {{ __('organization_users_settings.table_no_pending') }}
        </td>
      </TuTableRow>

      <template v-else-if="!isMobileView">
        <TuTableRow
          v-for="user in users"
          :key="user.email"
          class="_user_row"
        >
          <td class="org-user-pending-table__clamp-cell _user_email">
            {{ user.email }}
          </td>
          <td class="org-user-pending-table__narrow-cell _user_role">
            {{ Capitalize(user.invited_as) }}
          </td>
          <td class="org-user-pending-table__narrow-cell _user_date_invited">
            {{ formatDateInvited(user.created_at) }}
          </td>
          <td class="org-user-pending-table__action-cell">
            <template v-if="!props.disabled">
              <OrgUserPendingActionDropdown
                class="_user_actions"
                :disabled="busyWith === user.email"
                @resend="doResend(user)"
                @cancel="doCancel(user)"
              />
            </template>
            <template v-else>
              <span>—</span>
            </template>
          </td>
        </TuTableRow>
      </template>

      <template v-else>
        <TuTableRow
          v-for="user in users"
          :key="user.email"
          class="_user_row"
        >
          <td colspan="4">
            <div class="org-user-pending-table__cell-mobile org-user-pending-table__cell-mobile--full">
              <p class="org-user-pending-table__cell-mobile-content _user_email">
                {{ user.email }}
              </p>
            </div>
            <div class="org-user-pending-table__cell-mobile">
              <p class="org-user-pending-table__cell-mobile-title">
                {{ __('organization_users_settings.table_invited_as') }}:
              </p>
              <p class="org-user-pending-table__cell-mobile-content _user_role">
                {{ Capitalize(user.invited_as) }}
              </p>
            </div>
            <div class="org-user-pending-table__cell-mobile">
              <p class="org-user-pending-table__cell-mobile-title">
                {{ __('organization_users_settings.table_date_invited') }}:
              </p>
              <p class="org-user-pending-table__cell-mobile-content _user_date_invited">
                {{ formatDateInvited(user.created_at) }}
              </p>
            </div>
            <div class="org-user-pending-table__cell-mobile">
              <p class="org-user-pending-table__cell-mobile-title">
                {{ __('organization_users_settings.table_action') }}:
              </p>
              <div class="org-user-pending-table__cell-mobile-content">
                <template v-if="!props.disabled">
                  <OrgUserPendingActionDropdown
                    class="_user_actions"
                    :disabled="busyWith === user.email"
                    @resend="doResend(user)"
                    @cancel="doCancel(user)"
                  />
                </template>
                <template v-else>
                  <span>—</span>
                </template>
              </div>
            </div>
          </td>
        </TuTableRow>
      </template>
    </TuTable>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-user-pending-table {
  &__action-cell {
    width: 10%;
    white-space: nowrap;
  }

  &__narrow-cell {
    width: 15%;
    white-space: nowrap;
  }

  &__clamp-cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__cell-mobile {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: spacing.$xxxs;

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &--full {
      grid-template-columns: 1fr;
    }
  }

  &__cell-mobile-title {
    font-weight: typography.$font-weight-bold;
  }

  &__cell-mobile-title,
  &__cell-mobile-content {
    margin: 0;
  }
}
</style>
